import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { List as ImmutableList } from 'immutable';
import { headerIds } from '../constants-types/constants';
import { PI } from '../constants-types/PI.model';
import { List } from '../constants-types/PiList.model';
import { Pattern } from '../core-data-model/Pattern.model';
import {
    Person,
    PizzaPlaygroundSessionPersonWithVisibility
} from '../core-data-model/Person.model';
import {
    alignmentResponseData,
    PizzaPlaygroundSession,
    preferredWorkStyle,
    teamActionRecommendations
} from '../core-data-model/Strategy.model';
import {
    SelfServeTeamMemberServerResponse,
    StrategySurveyDefinitionServer,
    StrategySurveyQuestion
} from '../core-data-model/StrategyApi.serverinterface';
import { UserUpdate } from '../core-data-model/User.model';
import { UserCreateAccountProvisioningServer } from '../core-data-model/UserApi.serverinterface';
import { notificationOnError } from '../core-services/Notifications.service';
import { HttpHeaderContentType } from '../shared/Enums';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';
import { PizzaPlaygroundApiTransformers } from './PizzaPlaygroundApi.datatransform';
import {
    PizzaPlaygroundSessionPost,
    PizzaPlaygroundSessionPostServer,
    PizzaPlaygroundSessionServer,
    teamActionRecommendationsServer,
    teamSummaryShareLinkServer,
    UpdatePizzaPlaygroundSessionPost,
    UpdatePizzaPlaygroundSessionPostServer
} from './PizzaPlaygroundApi.serverinterface';
import { UserV4Transformer } from './UserApi.datatransform';

/*
The PizzaPlayground files are left in the core-services instead of DesignExplorer because these files are not exclusive to Design app anymore.
The Teams Api is currently consuming getPizzaPlaygroundSessionByTeamId and the list might grow further in the future.
Also, moving this file into @TeamPizza/DesingExplorer will introduce a cycle between TeamPizza <-> Core-Services or even Teams <-> TeamPizza
*/
@Injectable({
    providedIn: 'root'
})
export class PizzaPlaygroundApi {
    private designApiVersion = 'v5';

    constructor(
        private api: ApiService,
        private transformers: PizzaPlaygroundApiTransformers,
        private userApiTransform: UserV4Transformer,
        private userProfile: UserProfileSettingsService,
        private TR: TranslationService
    ) {}

    @notificationOnError()
    async getPizzaPlaygroundSessions(
        companyId: string = this.userProfile.currentCompany
    ): Promise<List<PizzaPlaygroundSession>> {
        const [sessions] = await this.api.get<PizzaPlaygroundSessionServer[]>(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsessions/`,
            null,
            null,
            PI.appSettings.designApiUrl
        );
        return ImmutableList(
            sessions.map((session) =>
                this.transformers.transformStrategyPlaygroundSession(session)
            )
        );
    }

    async getPizzaPlaygroundTotalUsedSessionsCount(
        companyId: string = this.userProfile.currentCompany
    ) {
        const responseHeaders: HttpHeaders = await this.api.head(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundtotalsessions/`,
            PI.appSettings.designApiUrl
        );

        return parseInt(responseHeaders.get(headerIds.totalCount));
    }

    async getPizzaPlaygroundSession(
        sessionId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<PizzaPlaygroundSession> {
        const [server] = await this.api.get<PizzaPlaygroundSessionServer>(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/${sessionId}`,
            null,
            null,
            PI.appSettings.designApiUrl
        );
        return this.transformers.transformStrategyPlaygroundSession(server);
    }

    async getPizzaPlaygroundSessionByTeamId(
        teamId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<PizzaPlaygroundSession> {
        const [server] = await this.api.get<PizzaPlaygroundSessionServer>(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/team/${teamId}`,
            null,
            null,
            PI.appSettings.designApiUrl
        );
        if (server) {
            return this.transformers.transformStrategyPlaygroundSession(server);
        }
        return null;
    }

    async createPizzaPlaygroundSession(
        pizzaPlaygroundSessionPost: PizzaPlaygroundSessionPost,
        companyId: string = this.userProfile.currentCompany
    ): Promise<PizzaPlaygroundSession> {
        const payload: PizzaPlaygroundSessionPostServer = {
            companyId: companyId,
            sessionName: pizzaPlaygroundSessionPost.sessionName,
            selectedStrategyIds: pizzaPlaygroundSessionPost.selectedStrategyIds,
            personIdsWithColor: pizzaPlaygroundSessionPost.personIdsWithColor,
            teamId: pizzaPlaygroundSessionPost.teamId,
            teamFunctionType: pizzaPlaygroundSessionPost.teamFunctionType,
            otherTeamFunction: pizzaPlaygroundSessionPost.otherTeamFunction,
            crossFunctionId: pizzaPlaygroundSessionPost.crossFunctionId,
            teamImprovementIds: pizzaPlaygroundSessionPost.teamImprovementIds,
            otherTeamImprovement:
                pizzaPlaygroundSessionPost.otherTeamImprovement
        };
        const [server] = await this.api.post<
            PizzaPlaygroundSessionPostServer,
            PizzaPlaygroundSessionServer
        >(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession`,
            payload,
            PI.appSettings.designApiUrl
        );
        return this.transformers.transformStrategyPlaygroundSession(server);
    }

    async updatePizzaPlaygroundSession(
        sessionId: string,
        sessionUpdate: UpdatePizzaPlaygroundSessionPost,
        companyId: string = this.userProfile.currentCompany
    ): Promise<PizzaPlaygroundSession> {
        const url = `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/${sessionId}`;
        const payload: UpdatePizzaPlaygroundSessionPostServer = {
            sessionName: sessionUpdate.sessionName,
            selectedStrategyIds: sessionUpdate.selectedStrategyIds,
            personIdsWithColor: sessionUpdate.personIdsWithColor,
            objectiveType: sessionUpdate.objectiveType,
            selectedActivities: sessionUpdate.selectedActivities,
            strategyType: sessionUpdate.strategyType,
            teamType: sessionUpdate.teamType,
            teamFunctionType: sessionUpdate.teamFunctionType,
            otherTeamFunction: sessionUpdate.otherTeamFunction,
            crossFunctionId: sessionUpdate.crossFunctionId,
            teamImprovementIds: sessionUpdate.teamImprovementIds,
            otherTeamImprovement: sessionUpdate.otherTeamImprovement,
            isStrategyAutoSelectionEnabled:
                sessionUpdate.isStrategyAutoSelectionEnabled,
            teamActions: sessionUpdate.teamActions,
            customActions: sessionUpdate.customActions,
            actionTeamType: sessionUpdate.actionTeamType,
            shareLinkGuid: sessionUpdate.shareLinkGuid
        };
        const [server] = await this.api.patch<
            UpdatePizzaPlaygroundSessionPost,
            PizzaPlaygroundSessionServer
        >(url, payload, PI.appSettings.designApiUrl);
        return this.transformers.transformStrategyPlaygroundSession(server);
    }

    async deletePizzaPlaygroundSession(
        sessionId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<void> {
        await this.api.delete(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/${sessionId}`,
            null,
            PI.appSettings.designApiUrl
        );
        return;
    }

    getTeamSummaryReportPage(
        session: PizzaPlaygroundSession,
        leadershipObjectives: StrategySurveyQuestion[],
        hydratedPeople: List<PizzaPlaygroundSessionPersonWithVisibility>,
        languageLocale = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ) {
        const url = encodeURIComponent(`imageservice/teamsummaryreport`);

        return this.api.getResourceUrlWithPdfReference(
            `/api/${this.designApiVersion}/companies/${companyId}/teams/${session.teamId}/teamsummaryreport?url=${url}&fileName=TeamSummaryReport&languageLocale=${languageLocale}`,
            HttpHeaderContentType.PDF,
            true,
            this.transformers.prepareTeamReport(
                session,
                leadershipObjectives,
                hydratedPeople
            ),
            PI.appSettings.designApiUrl
        );
    }

    async createSelfServePerson(person: UserUpdate): Promise<Person> {
        const payload =
            this.userApiTransform.createClientToAccountProvisioningServer(
                person
            );

        const [server] = await this.api.post<
            UserCreateAccountProvisioningServer,
            SelfServeTeamMemberServerResponse
        >(
            `/api/v4/companies/${this.userProfile.currentCompany}/selfserviceprovisioning/initiatedesignteammember`,
            payload
        );

        return this.transformers.tranformSelfServeTeamMemberFromPost(
            server,
            person
        );
    }

    async getStrategyAssessmentQuestions(
        companyId: string = this.userProfile.currentCompany
    ): Promise<StrategySurveyQuestion[]> {
        const [server] = await this.api.get<StrategySurveyDefinitionServer>(
            `/api/${this.designApiVersion}/companies/${companyId}/strategysurveys/definition`,
            null,
            null,
            PI.appSettings.designApiUrl
        );

        return server.questions;
    }

    async hasSummaryAccess(
        sessionId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<boolean> {
        const [server] = await this.api.get<boolean>(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/${sessionId}/summaryaccess`,
            null,
            null,
            PI.appSettings.designApiUrl
        );
        return server;
    }

    async createTeamSummaryLink(
        redirectUrl: string,
        session: PizzaPlaygroundSession,
        companyId: string = this.userProfile.currentCompany
    ): Promise<string> {
        const payload = this.transformers.transformTeamSummaryShareLinkToServer(
            companyId,
            redirectUrl,
            session
        );
        const [server] = await this.api.post<
            teamSummaryShareLinkServer,
            string
        >(
            `/api/${this.designApiVersion}/companies/${companyId}/sharelink`,
            payload,
            PI.appSettings.designApiUrl
        );
        return server;
    }

    async getRecommendedActionsByTeamId(
        teamId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<teamActionRecommendations> {
        const [server] = await this.api.get<teamActionRecommendationsServer>(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/team/${teamId}/actionrecommendations`,
            null,
            null,
            PI.appSettings.designApiUrl
        );
        return this.transformers.transformTeamActionRecommendations(server);
    }

    async getPreferredWorkStyle(
        selfPattern: Pattern,
        companyId: string = this.userProfile.currentCompany
    ): Promise<preferredWorkStyle> {
        const [server] = await this.api.get<preferredWorkStyle>(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/preferredworkstyle?A=${selfPattern.A}&B=${selfPattern.B}&C=${selfPattern.C}&D=${selfPattern.D}`,
            null,
            null,
            PI.appSettings.designApiUrl
        );
        return server;
    }

    async sendAlignmentPromptData(
        promptData: alignmentResponseData,
        sessionId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<void> {
        promptData.userId = this.userProfile.myUserId;
        const [server] = await this.api.post(
            `/api/${this.designApiVersion}/companies/${companyId}/strategyplaygroundsession/${sessionId}/alignmentresponse`,
            promptData,
            PI.appSettings.designApiUrl
        );
        return server;
    }
}
